import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { MatchVm } from '../../utils/api';
import { insightsIsLiveStatus, insightsScoreText } from './insightsAccess';
import { ClubMatchView, toClubMatchView } from './insightsClubSummary';

interface Props {
    matches: MatchVm[];
    ourTeamIds: number[];
    /** When true, fixture line shows vs/at opponent; otherwise Home vs Away names. */
    clubPerspective?: boolean;
    emptyText?: string;
}

const matchHref = (matchId: number): string =>
    Routes.ROUTE_INSIGHTS_MATCH.replace(ReplaceStrings.MATCH_ID, matchId.toString());

/**
 * Shared When / Fixture / Score table for Overview feed and Matches tab.
 */
const InsightsMatchTable: React.FC<Props> = ({
    matches,
    ourTeamIds,
    clubPerspective = true,
    emptyText = 'No matches for this selection.',
}) => {
    if (matches.length === 0) {
        return <div className="insightsEmpty">{emptyText}</div>;
    }

    return (
        <div className="insightsTableScroll">
            <table className="insightsMatchTable">
                <thead>
                    <tr>
                        <th>When</th>
                        <th>Fixture</th>
                        <th>Score</th>
                    </tr>
                </thead>
                <tbody>
                    {matches.map((match: MatchVm) => {
                        const view: ClubMatchView | undefined = toClubMatchView(match, ourTeamIds);
                        const fixtureTitle = clubPerspective
                            ? view
                                ? `${view.isHome ? 'vs' : 'at'} ${view.opponentName}`
                                : `${match.homeTeamDisplayName} vs ${
                                      match.awayTeamDisplayName || 'TBD'
                                  }`
                            : `${match.homeTeamDisplayName} vs ${
                                  match.awayTeamDisplayName || 'TBD'
                              }`;

                        return (
                            <tr key={match.id}>
                                <td>
                                    <Link className="insightsMatchLink" to={matchHref(match.id)}>
                                        <div className="insightsDate">
                                            {match.startDate
                                                ? moment(match.startDate).format('DD MMM YY HH:mm')
                                                : '-'}
                                        </div>
                                    </Link>
                                </td>
                                <td>
                                    <Link className="insightsMatchLink" to={matchHref(match.id)}>
                                        <div className="insightsTeams">{fixtureTitle}</div>
                                        <div className="insightsMeta">
                                            {match.competitionDisplayName}
                                            {match.round ? ` · ${match.round}` : ''}
                                        </div>
                                    </Link>
                                </td>
                                <td>
                                    <Link className="insightsMatchLink" to={matchHref(match.id)}>
                                        <div className="insightsScore">
                                            {insightsScoreText(match)}
                                        </div>
                                        {view?.result ? (
                                            <span
                                                className={`insightsResultBadge is-${view.result.toLowerCase()}`}
                                            >
                                                {view.result}
                                            </span>
                                        ) : match.status ? (
                                            <span
                                                className={
                                                    insightsIsLiveStatus(match)
                                                        ? 'insightsStatus isLive'
                                                        : 'insightsStatus'
                                                }
                                            >
                                                {match.status}
                                            </span>
                                        ) : null}
                                    </Link>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default InsightsMatchTable;
